<template>
    <div>
        <p class="text-sm text-gray-500 mb-5">
            {{
                translations?.fieldsNotice ||
                'Please complete all fields correctly, or we will be unable to process your payout request.'
            }}
        </p>
        <div class="grid grid-cols-3 gap-8 gap-y-6">

            <div class="col-span-3">
                <div class="bg-gray-200 inline-flex mb-1 p-1 rounded space-x-1 w-full z-0">
                    <label
                        class="bg-white opacity-100 opacity-60 hover:opacity-80 rounded flex-1 py-3 px-5 cursor-pointer">
                        <input type="radio" name="payment_option" value="1" class="sr-only">
                        <div class="text-sm font-medium uppercase">
                            <div class="opacity-60 text-xs normal-case font-normal">{{ translations?.payout || 'Payout' }}</div>
                            {{ amount_with_units_formatted }}
                        </div>
                    </label>
                </div>
            </div>

            <div class="col-span-3">
                <div class="bg-gray-200 inline-flex mb-1 p-1 rounded space-x-1 w-full z-0">
                    <label v-if="payment_settings.gateway_paypal" @click="payment_settings.gateway='paypal'"
                           class="rounded flex-1 p-2 cursor-pointer"
                           :class="payment_settings.gateway === 'paypal' ? 'bg-white' : 'opacity-60 hover:opacity-80'">
                        <div class="flex justify-center items-center text-sm font-medium">PayPal
                        </div>
                    </label>
                    <label v-if="payment_settings.gateway_bank"
                           @click="payment_settings.gateway='bank_transfer'"
                           class="rounded flex-1 p-2 cursor-pointer"
                           :class="payment_settings.gateway === 'bank_transfer' ? 'bg-white' : 'opacity-60 hover:opacity-80'">
                        <div class="flex justify-center items-center text-sm font-medium">
                            {{ translations?.bank || 'Bank' }}
                        </div>
                    </label>
                    <label v-if="payment_settings.gateway_crypto"
                           @click="payment_settings.gateway='crypto'"
                           class="rounded flex-1 p-2 cursor-pointer"
                           :class="payment_settings.gateway === 'crypto' ? 'bg-white' : 'opacity-60 hover:opacity-80'">
                        <div class="flex justify-center items-center text-sm font-medium">
                            {{ translations?.crypto || 'Crypto' }}
                        </div>
                    </label>
                </div>
            </div>

            <validation-errors class="col-span-3" :errors="requestErrors" />

            <template v-if="payment_settings.gateway_bank && payment_settings.gateway === 'bank_transfer'">
                <div v-for="field in bank_transfer_fields" class="col-span-3">
                    <label class="block text-sm mb-1.5 opacity-80">
                        {{ bankTransferFieldTranslationMapping[field.key] || field.name }}
                    </label>
                    <input
                        v-if="field.type === 'text'"
                        :name="field.key"
                        type="text"
                        v-bind:class="(formErrors[field.key] ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                        class="block w-full rounded border-gray-300 text-sm focus:border-gray-400 focus:ring-0"
                        v-model="payment_settings.bank_transfer_info[field.key]"
                    >
                    <select
                        v-else-if="field.type === 'select'"
                        :name="field.key"
                        v-model="payment_settings.bank_transfer_info[field.key]"
                        v-bind:class="(formErrors[field.key] ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                        class="block w-full text-sm rounded border-gray-300 transition-shadow focus:ring-0 focus:border-gray-400"
                        @change="() => refreshBankRequirements(field)"
                    >
                        <option :value="null">--</option>
                        <option v-for="value in field.valuesAllowed" :value="value.key">{{ value.name }}</option>
                    </select>
                    <radio-group
                        v-else-if="field.type === 'radio'"
                        :name="field.key"
                        :model-value="payment_settings.bank_transfer_info[field.key]"
                        :options="field.valuesAllowed.map((singleValue) => ({value: singleValue.key, label: singleValue.name}))"
                        @update:model-value="(event) => {
                            payment_settings.bank_transfer_info[field.key]=event
                            refreshBankRequirements(field)
                        }"
                    ></radio-group>
                </div>
            </template>

            <template v-if="payment_settings.gateway_paypal && payment_settings.gateway === 'paypal'">

                <div class="col-span-3">
                    <label class="block text-sm mb-1.5 opacity-80">
                        {{ translations?.fullName || 'Full name' }}
                    </label>
                    <input type="text"
                           v-bind:class="(formErrors.paypal_name ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                           class="block w-full rounded border-gray-300 text-sm focus:border-gray-400 focus:ring-0"
                           v-model="payment_settings.paypal_info.name"
                           v-bind:placeholder="placeholders.paypal_name">
                </div>

                <div class="col-span-3">
                    <label class="block text-sm mb-1.5 opacity-80">
                        {{ translations?.payPalEmail || 'PayPal email' }}
                    </label>
                    <input type="text"
                           v-bind:class="(formErrors.paypal_username ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                           class="block w-full rounded border-gray-300 text-sm focus:border-gray-400 focus:ring-0"
                           v-model="payment_settings.paypal_info.username"
                           v-bind:placeholder="placeholders.paypal_username">
                </div>

                <div class="col-span-3">
                    <label class="block text-sm mb-1.5 opacity-80">
                        {{ translations?.address || 'Address' }}
                    </label>
                    <input type="text"
                           v-bind:class="(formErrors.paypal_address ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                           class="block w-full rounded border-gray-300 text-sm focus:border-gray-400 focus:ring-0"
                           v-model="payment_settings.paypal_info.address"
                           v-bind:placeholder="placeholders.paypal_address">
                </div>

                <div class="col-span-3">
                    <label class="block text-sm mb-1.5 opacity-80">
                        {{ translations?.taxIdText || 'Tax ID (if available)' }}
                    </label>
                    <input type="text"
                           v-bind:class="(formErrors.paypal_tax_id ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                           class="block w-full rounded border-gray-300 text-sm focus:border-gray-400 focus:ring-0"
                           v-model="payment_settings.paypal_info.tax_id"
                           v-bind:placeholder="placeholders.paypal_tax_id">
                </div>
            </template>

            <template v-if="payment_settings.gateway_crypto && payment_settings.gateway === 'crypto'">
                <div class="col-span-3">
                    <label class="block text-sm mb-1.5 opacity-80">
                        {{ translations?.crypto_type || 'Crypto type' }}
                    </label>
                    <select
                        v-model="payment_settings.crypto_info.type"
                        v-bind:class="(formErrors.crypto_type ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                        class="block w-full text-sm rounded border-gray-300 transition-shadow focus:ring-0 focus:border-gray-400"
                    >
                        <option :value="null">--</option>
                        <option v-for="value in preferred_settings.crypto_types ?? []" :value="value.value">{{ value.name }}</option>
                    </select>
                </div>

                <div class="col-span-3">
                    <label class="block text-sm mb-1.5 opacity-80">
                        {{ translations?.fullName || 'Full name' }}
                    </label>
                    <input type="text"
                           v-bind:class="(formErrors.crypto_name ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                           class="block w-full rounded border-gray-300 text-sm focus:border-gray-400 focus:ring-0"
                           v-model="payment_settings.crypto_info.name"
                           v-bind:placeholder="placeholders.crypto_name">
                </div>

                <div class="col-span-3">
                    <label class="block text-sm mb-1.5 opacity-80">
                        {{ translations?.wallet_address || 'Wallet address' }}
                    </label>
                    <input type="text"
                           v-bind:class="(formErrors.crypto_wallet_address ?? false) ? 'border-red-400' : 'border-gray-300 focus:border-gray-400'"
                           class="block w-full rounded border-gray-300 text-sm focus:border-gray-400 focus:ring-0"
                           v-model="payment_settings.crypto_info.wallet_address"
                           v-bind:placeholder="placeholders.wallet_address">
                </div>
            </template>

            <file-upload
                v-if="payout_invoice_requirements === 'required' || payout_invoice_requirements === 'optional' || payout_invoice_requirements === 'generate_automatically'"
                class="col-span-3 rounded"
                :class="(formErrors.invoice ?? false) ? 'border border-red-400' : ''"
                :path="invoicePath ?? null"
                :src="invoiceUrl ?? null"
                accept="image/*, .pdf"
                :title="invoiceFileName || (payout_invoice_requirements === 'generate_automatically' ? (translations?.generateInvoice || 'Generate an invoice') : (translations?.uploadInvoice || 'Upload an invoice (PDF or Image)'))"
                name="invoice"
                @upload:done="uploadDone"
                @delete:done="() => {invoiceUrl = null; invoicePath = null}"
                @upload:error="fileUploadValidationErrors = $event"
                :upload_url="payoutInvoiceUploadUrl"
                :delete_url="payoutInvoiceDeleteUrl"
                :dynamic_action_buttons="true"
                :show_upload_button="payout_invoice_requirements!=='generate_automatically'"
                :additional_data="{payoutId: payout_id}"
            >
                <template v-slot:empty_placeholder>
                    <div class="">
                        <span class="text-sm text-gray-500 hidden">No file</span>
                        <icon name="file-missing" size="24" />
                    </div>
                </template>

                <template v-slot:uploaded_placeholder>
                    <div class="">
                        <span class="text-sm text-gray-500 hidden">File present</span>
                        <icon name="file" size="24" />
                    </div>
                </template>
            </file-upload>
            <validation-errors class="col-span-3" :errors="fileUploadValidationErrors" />

            <div v-if="payout_invoice_requirements === 'generate_automatically'" class="col-span-3">
                <InvoicePreview
                    :payout-id="payout_id"
                    :from-company="billFromDetails"
                    :to-company="bill_to_details"
                    :payout-method="payment_settings.gateway"
                    :invoice-number="'to be generated'"
                    :items="[{ name: 'Commission for payout', quantity: 1, unitCost: amount_with_units_formatted }]"
                    :currency="amount_unit"
                    :amount-with-currency-formatted="amount_with_units_formatted"
                    :notes="'Thanks for being an awesome customer!'"
                    :terms="'Please pay by the due date.'"
                    :tax="5"
                    @invoice-generated="handleInvoiceGenerated"
                    @request-errors="requestErrors = $event"
                    :translations="translations"
                />
            </div>

            <div class="flex justify-between items-center col-span-3">
                <toggle name="save_payment_info"
                        :modelValue="payment_settings.save_for_future_payments"
                        @update:modelValue="changeFutureValue"
                />
                <span class="flex flex-col flex-grow -mt-px" id="save_payment_info">
                    <span class="text-sm">
                        {{ translations?.saveForFuturePayouts || 'Save for future payouts' }}
                    </span>
                </span>
            </div>

        </div>
    </div>
</template>
<script>
import ValidationErrors from "../../common/ValidationErrors.vue";
import Icon from "../../../elements/Icon.vue";
import InvoicePreview from "./InvoicePreview.vue";

export default {
    components: {InvoicePreview, Icon, ValidationErrors},
    props: {
        countries: { type: Array, default: () => [] },
        placeholders: { type: Object, default: () => ({}) },
        amount_unit: { type: String, default: '' },
        amount_with_units_formatted: { type: String, default: '' },
        preferred_settings: { type: Object, default: () => ({}) },
        available_methods: { type: Array, default: () => [] },
        translations: { type: Object, default: () => ({}) },
        requestErrors: {},
        refresh_bank_requirements_url: { type: String, default: '' },
        payout_id: { type: [Number, String], default: null },
        payout_invoice_path: { type: String, default: null },
        payout_invoice_src: { type: String, default: null },
        payout_invoice_requirements: { type: String, default: 'none' },
        bill_to_details: { type: Object, default: () => ({
                                                    first_name: '',
                                                    last_name: '',
                                                    company_name: '',
                                                    address: '',
                                                    city: '',
                                                    zip: '',
                                                    country: '',
                                                    state: '',
                                                }
                                            )},
    },
    data() {

        let gatewayCandidate = this.preferred_settings.gateway || 'paypal';
        gatewayCandidate = this.available_methods.includes(gatewayCandidate) ? gatewayCandidate : this.available_methods[0] || 'paypal';

        return {
            formErrors: {},
            payment_settings: {

                gateway_paypal: this.available_methods.includes('paypal'),
                gateway_bank: this.available_methods.includes('bank_transfer'),
                gateway_crypto: this.available_methods.includes('crypto'),

                gateway: gatewayCandidate,
                save_for_future_payments: this.preferred_settings.save_for_future_payments || true,
                bank_transfer_info: {...this.preferred_settings.bank_transfer_info},
                paypal_info: {
                    name: this.preferred_settings.paypal_info?.name || null,
                    username: this.preferred_settings.paypal_info?.username || null,
                    address: this.preferred_settings.paypal_info?.address || null,
                    tax_id: this.preferred_settings.paypal_info?.tax_id || null,
                },
                crypto_info: {
                    type: this.preferred_settings.crypto_info?.type || null,
                    name: this.preferred_settings.crypto_info?.name || null,
                    wallet_address: this.preferred_settings.crypto_info?.wallet_address || null,
                }
            },
            bank_transfer_fields: this.preferred_settings.bank_transfer_fields || [],
            refreshBankRequirementsLoading: false,
            payoutInvoiceUploadUrl: route('partners_int.payouts.invoice-file-upload'),
            payoutInvoiceDeleteUrl: route('partners_int.payouts.invoice-file-delete'),
            invoiceUrl: this.payout_invoice_src || null,
            invoicePath: this.payout_invoice_path || null,
            fileUploadValidationErrors: null,
            bankTransferFieldTranslationMapping: {
                targetCurrency: this.translations?.targetCurrency,
                accountHolderName: ((this.translations?.accountHolder ?? '') + ' ' + (this.translations?.fullName ?? '')).trim(),
                accountOrIban: this.translations?.accountNumber + '/' + this.translations?.iban,
                routingNumber: this.translations?.routingNumber,
                swiftBIC: this.translations?.swiftBicCode,
                bankName: this.translations?.bankName,
                bankCountry: ((this.translations?.bank ?? '') + ' ' + (this.translations?.country ?? '').toLowerCase()).trim(),
                bankCity: ((this.translations?.bank ?? '') + ' ' + (this.translations?.city ?? '').toLowerCase()).trim(),
                bankAddress: ((this.translations?.bank ?? '') + ' ' + (this.translations?.address ?? '').toLowerCase()).trim(),
                bankAddressPostalCode: ((this.translations?.bank ?? '') + ' ' + (this.translations?.postalCode ?? '').toLowerCase()).trim(),
                legalType: this.translations?.recipientType,
                accountNumber: this.translations?.accountNumber,
                BIC: this.translations?.swiftBicCode,
                IBAN: this.translations?.iban,
                accountType: this.translations?.accountType,
                address__country: this.translations?.country,
                address__city: this.translations?.city,
                address__firstLine: this.translations?.address,
                address__postCode: this.translations?.postalCode,
            }
        }
    },
    methods: {
        uploadDone(data) {
            this.invoiceUrl = data.invoice.url;
            this.invoicePath = data.invoice.path;
        },
        handleInvoiceGenerated(data) {
            this.invoiceUrl = data.invoiceUrl;
            this.invoicePath = data.invoicePath;
        },
        getAmountUnit() {
            return this.amount_unit;
        },
        getPaymentSettings() {
            return this.payment_settings;
        },
        changeFutureValue(value) {
            this.payment_settings.save_for_future_payments = value;
        },
        getInvoicePath() {
            return this.invoicePath;
        },
        validatePaymentInfo() {

            let valid = true;
            this.formErrors = {};

            if (this.payment_settings.gateway === 'paypal') {
                if (this.inputIsEmpty(this.payment_settings.paypal_info.name)) {
                    valid = false;
                    this.formErrors.paypal_name = true;
                }
                if (this.inputIsEmpty(this.payment_settings.paypal_info.address)) {
                    valid = false;
                    this.formErrors.paypal_address = true;
                }
                if (this.inputIsEmpty(this.payment_settings.paypal_info.username) || /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/.test(this.payment_settings.paypal_info.username) !== true) {
                    valid = false;
                    this.formErrors.invoice = true;
                }
            }

            if (this.payment_settings.gateway === 'bank_transfer') {
                this.bank_transfer_fields.forEach((field) => {
                    // If required
                    if(field.required && this.inputIsEmpty(this.payment_settings.bank_transfer_info[field.key])) {
                        valid = false;
                        this.formErrors[field.key] = true;
                    }
                });
            }

            if (this.payment_settings.gateway === 'crypto') {
                if (this.inputIsEmpty(this.payment_settings.crypto_info.type)) {
                    valid = false;
                    this.formErrors.crypto_type = true;
                }
                if (this.inputIsEmpty(this.payment_settings.crypto_info.name)) {
                    valid = false;
                    this.formErrors.crypto_name = true;
                }
                if (this.inputIsEmpty(this.payment_settings.crypto_info.wallet_address)) {
                    valid = false;
                    this.formErrors.crypto_wallet_address = true;
                }
            }

            if (this.payout_invoice_requirements === 'required' && (!this.invoiceUrl || !this.invoicePath)) {
                valid = false;
                this.formErrors.invoice = true;
            }

            return valid;
        },
        inputIsEmpty(value) {
            return (value ?? '').trim().length < 1;
        },
        refreshBankRequirements(field) {
            if (!field.refreshRequirementsOnChange || this.refreshBankRequirementsLoading) {
                return;
            }

            this.refreshBankRequirementsLoading = true;

            axios.post(this.refresh_bank_requirements_url, {
                amount_unit: this.getAmountUnit(),
                settings: {
                    requested_payment_settings: this.getPaymentSettings()
                }
            })
                .then(response => {
                    const status = parseInt(response.data.status ?? -1);
                    if (status === 1) {
                        this.setBankTransferFields(response.data.bank_transfer_fields)
                    }
                })
                .catch(error => {
                    if (error.response && error.response.data && error.response?.data?.errors) {
                        this.$emit('request-errors', error.response.data.errors)
                    }
                })
                .finally(() => this.refreshBankRequirementsLoading = false);
        },
        setBankTransferFields(bank_transfer_fields) {
            this.bank_transfer_fields = bank_transfer_fields
        },
    },
    watch: {
        refreshBankRequirementsLoading: function (newValue, oldValue) {
            this.$emit('loading-refresh-bank-requirements', newValue)
        }
    },
    computed: {
        invoiceFileName() {
            return this.invoicePath ? this.invoicePath.split('/').pop() : null;
        },
        billFromDetails() {
            const gatewayMapping = {
                'paypal': {
                    name: this.payment_settings.paypal_info.name || '',
                    email: this.payment_settings.paypal_info.username || '',
                    address: this.payment_settings.paypal_info.address  || '',
                    taxId: this.payment_settings.paypal_info.tax_id  || '',
                },
                'bank_transfer': {
                    name: this.payment_settings.bank_transfer_info.accountHolderName  || '',
                    address: this.payment_settings.bank_transfer_info.address__firstLine  || '',
                    city: this.payment_settings.bank_transfer_info.address__city  || '',
                    country: this.payment_settings.bank_transfer_info.address__country  || '',
                    postalCode: this.payment_settings.bank_transfer_info.address__postCode  || '',
                    taxId: this.payment_settings.bank_transfer_info.taxId  || '',
                },
                'crypto': {
                    name: this.payment_settings.crypto_info.name  || '',
                    address: this.payment_settings.crypto_info.wallet_address  || '',
                    taxId: this.payment_settings.crypto_info.taxId  || '',
                }
            };

            return gatewayMapping[this.payment_settings.gateway];
        }
    }
}
</script>
